/**
 * SubmissionContentViewer Component
 *
 * Polymorphic content viewer that routes to appropriate viewer based on content type.
 * Fetches submission content from backend and displays using specialized viewers:
 * - PDFViewer for PDF documents
 * - JSONViewer for Moodle quiz responses
 * - CodeViewer for code files (JS, Python, etc.)
 * - HTMLViewer for HTML documents
 * - TextViewer for plain text files
 *
 * This component embodies the "intelligent viewer" concept - backend tells frontend
 * HOW to render content via content_type and display_data structure.
 */

import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import JSONViewer from './JSONViewer';
import CodeViewer from './CodeViewer';
import HTMLViewer from './HTMLViewer';
import PDFViewer from './PDFViewer';
import TextViewer from './TextViewer';

interface SubmissionContentViewerProps {
  submissionId: number;
}

interface ContentData {
  content_type: string;
  display_data: {
    // Moodle quiz data
    questions?: Array<{
      number: number;
      answer: string;
      type: 'javascript' | 'html' | 'css' | 'text';
    }>;
    responses?: { [key: string]: string };
    metadata?: { [key: string]: any };

    // PDF data
    file_url?: string;
    filename?: string;

    // Code data
    code?: string;
    language?: string;

    // Text data
    content?: string;
  };
  metadata?: { [key: string]: any };
}

const SubmissionContentViewer: React.FC<SubmissionContentViewerProps> = ({ submissionId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentData, setContentData] = useState<ContentData | null>(null);

  useEffect(() => {
    const fetchContent = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.get(`/api/submissions/${submissionId}/content/`);
        setContentData(response.data);
      } catch (err: any) {
        console.error('Error fetching submission content:', err);
        setError(err.response?.data?.error || 'Failed to load submission content');
      } finally {
        setLoading(false);
      }
    };

    if (submissionId) {
      fetchContent();
    }
  }, [submissionId]);

  // Loading state
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert severity="error">
        {error}
      </Alert>
    );
  }

  // No content data
  if (!contentData) {
    return (
      <Alert severity="warning">
        No content data available for this submission
      </Alert>
    );
  }

  // Route to appropriate viewer based on content_type
  const { content_type, display_data } = contentData;

  switch (content_type) {
    case 'moodle_quiz':
      return (
        <Box sx={{ maxWidth: '100%', overflow: 'auto' }}>
          <JSONViewer
            questions={display_data.questions}
            responses={display_data.responses}
            metadata={display_data.metadata}
          />
        </Box>
      );

    case 'pdf':
      return (
        <PDFViewer
          file_url={display_data.file_url || ''}
          filename={display_data.filename}
        />
      );

    case 'code':
    case 'html':
      // Use HTMLViewer for HTML, CodeViewer for other code
      if (content_type === 'html' && display_data.code) {
        return (
          <HTMLViewer
            code={display_data.code}
            filename={display_data.filename}
          />
        );
      }
      return (
        <CodeViewer
          code={display_data.code || ''}
          language={display_data.language || 'javascript'}
          filename={display_data.filename}
          showLineNumbers={true}
        />
      );

    case 'text':
      return (
        <TextViewer
          content={display_data.content || ''}
          filename={display_data.filename}
        />
      );

    default:
      return (
        <Alert severity="info">
          Unsupported content type: {content_type}
          <br />
          <Box component="pre" mt={1} sx={{ fontSize: '0.75rem', overflow: 'auto' }}>
            {JSON.stringify(display_data, null, 2)}
          </Box>
        </Alert>
      );
  }
};

export default SubmissionContentViewer;
